// /mnt/data/NascarProjections.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import DriverCardModal from "../../components/DriverCardModal";

/* ------------------------ config & constants ------------------------ */

const BASE = import.meta?.env?.BASE_URL ?? "/";

const SERIES_CONFIG = {
  cup: {
    title: "NASCAR Cup — DFS Projections",
    source: `${BASE}data/nascar/cup/latest/projections.json`,
    driverData: `${BASE}data/nascar/cup/latest/data.json`,
    practice: `${BASE}data/nascar/cup/latest/practice_cons.json`,
    pointDists: `${BASE}data/nascar/cup/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/cup`,
    csvPrefix: "cup_projections",
  },
  xfinity: {
    title: "NASCAR O'Reilly — DFS Projections",
    source: `${BASE}data/nascar/xfinity/latest/projections.json`,
    driverData: `${BASE}data/nascar/xfinity/latest/data.json`,
    practice: `${BASE}data/nascar/xfinity/latest/practice_cons.json`,
    pointDists: `${BASE}data/nascar/xfinity/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/xfinity`,
    csvPrefix: "xfinity_projections",
  },
  trucks: {
    title: "NASCAR Trucks — DFS Projections",
    source: `${BASE}data/nascar/trucks/latest/projections.json`,
    driverData: `${BASE}data/nascar/trucks/latest/data.json`,
    practice: `${BASE}data/nascar/trucks/latest/practice_cons.json`,
    pointDists: `${BASE}data/nascar/trucks/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/trucks`,
    csvPrefix: "trucks_projections",
  },
};

const DRIVER_ICON_SIZE = "h-5 w-5";
const LOGO_DK = `${BASE}logos/dk.png`;
const LOGO_FD = `${BASE}logos/fd.png`;
const SHOW_SOURCE = false;

/* Simulation details: only exported fields are offered; no tail estimates. */
const DETAIL_COLUMNS = [];
function addDetail(field, label, group, probability = false, lower = false) {
  DETAIL_COLUMNS.push({ field, label, group, probability, lower });
}
for (const [field, label] of [["WinPct", "Win"], ["Top3Pct", "Top 3"], ["Top5Pct", "Top 5"], ["Top10Pct", "Top 10"], ["Top20Pct", "Top 20"], ["DNF_Pct", "DNF"]]) {
  addDetail(field, `Sim ${label} %`, "Finish", true, field === "DNF_Pct");
}
addDetail("Finish_mean_raw", "Sim Avg Finish", "Finish", false, true);
addDetail("Finish_p10", "Sim Best Finish (P10)", "Finish", false, true);
addDetail("Finish_p50", "Sim Median Finish (P50)", "Finish", false, true);
addDetail("Finish_p90", "Sim Worst Finish (P90)", "Finish", false, true);
for (const [prefix, label, group] of [["LapsLed", "LL", "Laps led"], ["FastLaps", "FL", "Fastest laps"]]) {
  for (const [suffix, title] of [["mean", "Avg"], ["p10", "Floor P10"], ["p50", "Median P50"], ["p90", "Ceiling P90"], ["p95", "P95"], ["p99", "P99"]]) {
    addDetail(`${prefix}_${suffix}`, `Sim ${label} ${title}`, group);
  }
}
addDetail("MostLapsLedPct", "Sim Most Laps Led %", "Dominators", true);
addDetail("Led25PctRacePct", "Sim Lead 25%+ of Race %", "Dominators", true);
addDetail("Led50PctRacePct", "Sim Lead 50%+ of Race %", "Dominators", true);
for (const site of ["DK", "FD"]) {
  for (const [metric, title] of [["pts", "Total Pts"], ["PP", "Finish + PD Pts"], ["Dom", "Dom Pts"]]) {
    for (const [suffix, percentile] of [["mean", "Avg"], ["p10", "Floor P10"], ["p50", "Median P50"], ["p90", "Ceiling P90"], ["p95", "P95"], ["p99", "P99"]]) {
      addDetail(`${site}_${metric}_${suffix}`, `${site} Sim ${title} ${percentile}`, `${site} points`);
    }
  }
}
const DETAIL_BY_LABEL = Object.fromEntries(DETAIL_COLUMNS.map((c) => [c.label, c]));
const detailNumber = (value) => value === null || value === undefined || value === "" || typeof value === "boolean" ? null : Number.isFinite(Number(value)) ? Number(value) : null;
const detailValue = (c, value) => {
  if (!DETAIL_BY_LABEL[c]) return value ?? "";
  const n = detailNumber(value);
  return n === null ? "—" : `${n.toFixed(1)}${DETAIL_BY_LABEL[c].probability ? "%" : ""}`;
};

/* ------------------------ data fetch hook ------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (alive) {
          setData(j);
          setErr(null);
        }
      })
      .catch((e) => {
        if (alive) {
          setErr(e);
          setData(null);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err, loading };
}

/* ---------------- LAST UPDATED ---------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        }).then((x) => x.json());
        const iso =
          m?.updated_iso ||
          m?.updated_utc ||
          m?.updated ||
          m?.lastUpdated ||
          m?.timestamp;
        const ep = m?.updated_epoch;
        const d = iso
          ? new Date(iso)
          : Number.isFinite(ep)
          ? new Date(ep * 1000)
          : null;
        if (alive && d && !isNaN(d)) setUpdatedAt(d);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ----------------------------- helpers ----------------------------- */
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};
const isNumeric = (v) => Number.isFinite(num(v));
const cmp = (a, b, dir = "asc") => {
  const na = isNumeric(a);
  const nb = isNumeric(b);
  if (na && nb) {
    const va = num(a);
    const vb = num(b);
    return dir === "asc" ? va - vb : vb - va;
  }
  const sa = String(a ?? "").toLowerCase();
  const sb = String(b ?? "").toLowerCase();
  if (sa < sb) return dir === "asc" ? -1 : 1;
  if (sa > sb) return dir === "asc" ? 1 : -1;
  return 0;
};
const looksNumericCol = (c) =>
  /(^rank$|avg|average|proj|pp$|rtg|lev|own|pown|sal|ceiling|floor|dom|fin|val|^p?ll$|^p?fl$|qual|start|grid|pos|position|starting)/i.test(
    String(c).trim()
  ) || /^\d+$/.test(String(c));
const findDriverKey = (row) =>
  Object.keys(row || {}).find((k) => /^driver\b/i.test(k)) || "Driver";
const isDriverCol = (c) => /^driver\b/i.test(String(c));

const findStartPosCol = (row) => {
  const keys = Object.keys(row || {});
  // Common naming patterns across your NASCAR datasets
  return (
    keys.find((k) => /^(start|starting)\s*(pos|position)$/i.test(String(k).trim())) ||
    keys.find((k) => /^start\s*pos$/i.test(String(k).trim())) ||
    keys.find((k) => /^grid$/i.test(String(k).trim())) ||
    keys.find((k) => /^grid\s*pos(ition)?$/i.test(String(k).trim())) ||
    keys.find((k) => /^qual(ifying)?\s*(pos|position)$/i.test(String(k).trim())) ||
    keys.find((k) => /^starting\s*spot$/i.test(String(k).trim())) ||
    null
  );
};

// Finish-position points (1..40). Index 0 unused.
const DK_FINISH_PTS = [
  null,
  45, 42, 41, 40, 39, 38, 37, 36, 35, 34,
  32, 31, 30, 29, 28, 27, 26, 25, 24, 23,
  21, 20, 19, 18, 17, 16, 15, 14, 13, 12,
  10, 9, 8, 7, 6, 5, 4, 3, 2, 1,
];

const FD_FINISH_PTS = [
  null,
  43, 40, 38, 37, 36, 35, 34, 33, 32, 31,
  30, 29, 28, 27, 26, 25, 24, 23, 22, 21,
  20, 19, 18, 17, 16, 15, 14, 13, 12, 11,
  10, 9, 8, 7, 6, 5, 4, 3, 2, 1,
];


function colBrand(c) {
  const s = String(c).toLowerCase();
  if (/\bdk\b|\bdk\s|^dk/.test(s)) return "DK";
  if (/\bfd\b|\bfd\s|^fd/.test(s)) return "FD";
  return "NEUTRAL";
}
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const escapeCSV = (v) => {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const sumNums = (vals) =>
  vals.reduce((acc, v) => (Number.isFinite(v) ? acc + v : acc), 0);

const money = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : "–";
const oneDec = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 })
    : "–";

const oneDecMax = (n) =>
  Number.isFinite(n)
    ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })
    : "";

function pickCol(cols, patterns) {
  const list = cols || [];
  for (const p of patterns) {
    const re = p instanceof RegExp ? p : new RegExp(p, "i");
    const hit = list.find((c) => re.test(String(c)));
    if (hit) return hit;
  }
  return null;
}

function extractSiteCols(cols) {
  // Be forgiving: your JSON keys sometimes have spaces, sometimes not.
  const dk = {
    sal: pickCol(cols, [/^dk\s*sal/i, /\bdk\b.*\bsal/i, /\bsalary\b.*\bdk\b/i]),
    proj: pickCol(cols, [/^dk\s*proj/i, /\bdk\b.*\bproj/i]),
    val: pickCol(cols, [/^dk\s*val/i, /\bdk\b.*\bval/i, /\bvalue\b.*\bdk\b/i]),
    pown: pickCol(cols, [/^dk\s*pown/i, /\bdk\b.*\bpown/i, /\bdk\b.*\bown/i, /\bownership\b.*\bdk\b/i]),
    opt: pickCol(cols, [/^dk\s*opt/i, /\bdk\b.*\bopt/i, /\boptimal\b.*\bdk\b/i]),
  };
  const fd = {
    sal: pickCol(cols, [/^fd\s*sal/i, /\bfd\b.*\bsal/i, /\bsalary\b.*\bfd\b/i]),
    proj: pickCol(cols, [/^fd\s*proj/i, /\bfd\b.*\bproj/i]),
    val: pickCol(cols, [/^fd\s*val/i, /\bfd\b.*\bval/i, /\bvalue\b.*\bfd\b/i]),
    pown: pickCol(cols, [/^fd\s*pown/i, /\bfd\b.*\bpown/i, /\bfd\b.*\bown/i, /\bownership\b.*\bfd\b/i]),
    opt: pickCol(cols, [/^fd\s*opt/i, /\bfd\b.*\bopt/i, /\boptimal\b.*\bfd\b/i]),
  };
  return { dk, fd };
}

function rowMetric(row, col) {
  if (!col) return NaN;
  const v = row?.[col];
  return num(v);
}
/* normalize driver names for cross-file matching */
const keynorm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\b(jr|sr|ii|iii|iv)\b/g, "$1")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/* heat coloring rules */
const dirForCol = (colName) => {
  if (DETAIL_BY_LABEL[colName]) return DETAIL_BY_LABEL[colName].lower ? "lower" : "higher";
  const k = String(colName || "").toLowerCase().replace(/\s+/g, " ").trim();
  if (/^proj\s*fin$/i.test(colName)) return "lower";
  if (/\bsal(ary)?\b/i.test(colName)) return "lower";
  if (/\b(p?own%?|ownership)\b/i.test(k)) return "lower";
  if (
    /\b(pll|pfl)\b/i.test(k) ||
    /\b(pp)\b/.test(k) ||
    /\b(dom)\b/i.test(k) ||
    /\bfloor\b/i.test(k) ||
    /\bceiling\b/i.test(k)
  )
    return "higher";
  if (
    /\bproj(?!\s*fin)\b/i.test(k) ||
    /\bval(ue)?\b/i.test(k) ||
    /\brtg|rating\b/i.test(k) ||
    /\blev%?\b/i.test(k) ||
    /\bopt%?\b/i.test(k)
  )
    return "higher";
  return null;
};

/**
 * Theme-aware heatmap:
 *  - rdylgn: red → yellow → green (green = better)
 *  - blueorange: blue → white → orange (orange = better)
 */

function heatColor(min, max, v, dir, palette = "rdylgn", theme = "dark") {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (!Number.isFinite(v) || min === max || !dir) return null;

  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";

  if (palette === "rdylgn") {
    if (t < 0.5) {
      const u = t / 0.5;
      const h = 5 + u * 55;
      const s = isDark ? 75 : 70;
      const l = isDark ? 28 + u * 14 : 88 - u * 10;
      return `hsl(${h}, ${s}%, ${l}%)`;
    } else {
      const u = (t - 0.5) / 0.5;
      const h = 60 + u * 60;
      const s = isDark ? 65 : 60;
      const l = isDark ? 42 - u * 10 : 78 - u * 12;
      return `hsl(${h}, ${s}%, ${l}%)`;
    }
  }

  return null;
}

function heatTextColor(min, max, v, dir, theme = "dark") {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (!Number.isFinite(v) || min === max || !dir) return null;

  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";

  // orange/red -> yellow -> green
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  } else {
    const u = (t - 0.5) / 0.5;
    const h = 50 + u * 70;
    const s = isDark ? 88 : 80;
    const l = isDark ? 62 + u * 8 : 44 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
}

/* ----------------------------- mini UI pieces ----------------------------- */

function HoverHint({ text, side = "top" }) {
  const sideCls =
    side === "bottom"
      ? "top-full mt-2"
      : side === "left"
      ? "right-full mr-2 top-1/2 -translate-y-1/2"
      : side === "right"
      ? "left-full ml-2 top-1/2 -translate-y-1/2"
      : "bottom-full mb-2"; // top (default)

  return (
    <span
      className={[
        "pointer-events-none absolute z-[999]",
        "opacity-0 scale-[0.98] translate-y-0",
        "group-hover:opacity-100 group-hover:scale-100",
        "transition-all duration-150 ease-out",
        sideCls,
      ].join(" ")}
    >
      <span
        className={[
          "rounded-xl border px-2.5 py-1.5 text-[11px] font-semibold",
          "shadow-lg backdrop-blur",
          "whitespace-nowrap",
          "bg-slate-950/90 text-slate-100 border-slate-700/70",
        ].join(" ")}
      >
        {text}
      </span>
    </span>
  );
}


function SegTabs({ tabs, value, onChange, isDark }) {
  return (
    <div
      className={
        "inline-flex flex-wrap items-center gap-1 border-b " +
        (isDark
          ? "border-slate-800"
          : "border-slate-200")
      }
      role="tablist"
    >
      {tabs.map((t) => {
        const active = value === t.key;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            className={
              "h-9 whitespace-nowrap px-2.5 border-b-2 text-xs font-semibold transition " +
              (active
                ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                : isDark
                ? "border-transparent text-slate-400 hover:text-white"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function DriverPill({ name, iconSrc, onClick, right, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition " +
        (isDark
          ? "bg-slate-950/60 border-slate-800 hover:bg-slate-900/60"
          : "bg-white border-slate-200 hover:bg-slate-50")
      }
    >
      {iconSrc ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={iconSrc}
          className="h-5 w-5 object-contain"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="h-5 w-5 rounded-full bg-slate-700/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{name}</div>
      </div>
      {right ? <div className="text-xs opacity-80 tabular-nums">{right}</div> : null}
    </button>
  );
}

function MetricChip({ label, value, isBad, isDark }) {
  return (
    <div
      className={
        "rounded-lg border px-3 py-2 flex flex-col gap-0.5 " +
        (isBad
          ? isDark
            ? "bg-red-950/40 border-red-800/60"
            : "bg-red-50 border-red-200"
          : isDark
          ? "bg-slate-950/60 border-slate-800"
          : "bg-white border-slate-200")
      }
    >
      <div className={"text-[11px] font-semibold " + (isBad ? "text-red-300" : isDark ? "text-slate-400" : "text-slate-500")}>
        {label}
      </div>
      <div className={"text-sm font-extrabold tabular-nums " + (isBad ? "text-red-200" : "")}>{value}</div>
    </div>
  );
}

function summarizeGroup(rows, cols, driverKey) {
  const site = extractSiteCols(cols);
  const pick = (r, c) => (c ? num(r?.[c]) : NaN);
  const names = rows.map((r) => String(r?.[driverKey] ?? "")).filter(Boolean);

  const dk = {
    count: rows.length,
    sal: sumNums(rows.map((r) => pick(r, site.dk.sal))),
    proj: sumNums(rows.map((r) => pick(r, site.dk.proj))),
    pown: sumNums(rows.map((r) => pick(r, site.dk.pown))),
    opt: sumNums(rows.map((r) => pick(r, site.dk.opt))),
    valAvg: (() => {
      const vals = rows.map((r) => pick(r, site.dk.val)).filter(Number.isFinite);
      return vals.length ? sumNums(vals) / vals.length : NaN;
    })(),
  };
  const fd = {
    count: rows.length,
    sal: sumNums(rows.map((r) => pick(r, site.fd.sal))),
    proj: sumNums(rows.map((r) => pick(r, site.fd.proj))),
    pown: sumNums(rows.map((r) => pick(r, site.fd.pown))),
    opt: sumNums(rows.map((r) => pick(r, site.fd.opt))),
    valAvg: (() => {
      const vals = rows.map((r) => pick(r, site.fd.val)).filter(Number.isFinite);
      return vals.length ? sumNums(vals) / vals.length : NaN;
    })(),
  };

  return { names, dk, fd };
}

/* ----------------------------- page ----------------------------- */

export default function NascarProjections({ series = "cup" }) {
  const seriesKey = String(series || "cup").toLowerCase();
  const cfg = SERIES_CONFIG[seriesKey] || SERIES_CONFIG.cup;

  const SOURCE = cfg.source;
  const META = SOURCE.replace(/projections\.json$/, "meta.json");
  const DRIVER_DATA_SRC = cfg.driverData;
  const PRACTICE_SRC = cfg.practice;

  const POINT_DISTS_SRC = cfg.pointDists;

  const NUM_LOGO_BASE = cfg.numLogoBase;
  const CSV_PREFIX = cfg.csvPrefix;
  const TITLE = cfg.title;

  const { data, err, loading } = useJson(SOURCE);
  const { data: driverDataRaw } = useJson(DRIVER_DATA_SRC);
  const { data: practiceRaw } = useJson(PRACTICE_SRC);
  const { data: pointDistsRaw } = useJson(POINT_DISTS_SRC);
  const updatedAt = useLastUpdated(SOURCE, META);
  const { data: simSummaryRaw, err: simSummaryError, loading: simSummaryLoading } = useJson(
    SOURCE.replace(/projections\.json$/, "sim_summary.json")
  );
  const [showDetailed, setShowDetailed] = useState(false);
  const [detailGroup, setDetailGroup] = useState("All details");
  const simSummaryRows = useMemo(() => {
    const list = Array.isArray(simSummaryRaw) ? simSummaryRaw : simSummaryRaw?.rows || [];
    return Array.isArray(list) ? list.filter((r) => !r.Series || String(r.Series).toLowerCase() === seriesKey) : [];
  }, [simSummaryRaw, seriesKey]);
  const detailColumns = useMemo(() => DETAIL_COLUMNS.filter((c) =>
    simSummaryRows.some((r) => detailNumber(r[c.field]) !== null)
  ), [simSummaryRows]);
  const summaryByDriver = useMemo(() => new Map(simSummaryRows.map((r) => [keynorm(r.Driver), r])), [simSummaryRows]);


  const rows = useMemo(() => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
    return list.map((r) => {
      const sim = summaryByDriver.get(keynorm(r[findDriverKey(r)]));
      const extra = {};
      for (const c of detailColumns) {
        const value = detailNumber(sim?.[c.field]);
        extra[c.label] = value === null ? null : value * (c.probability ? 100 : 1);
      }
      return { ...r, ...extra };
    });
  }, [data, summaryByDriver, detailColumns]);

  const allColsRaw = useMemo(
    () => { const list = Array.isArray(data) ? data : data?.rows || []; return list.length ? Object.keys(list[0]) : []; },
    [data]
  );
  const driverKey = useMemo(() => findDriverKey(rows[0] || {}), [rows]);
  /* Driver -> car number map (from data.json) */
  const driverNumMap = useMemo(() => {
    const map = new Map();
    const arr = Array.isArray(driverDataRaw)
      ? driverDataRaw
      : Array.isArray(driverDataRaw?.rows)
      ? driverDataRaw.rows
      : Array.isArray(driverDataRaw?.data)
      ? driverDataRaw.data
      : [];
    if (!arr.length) return map;

    const findNumCol = (obj) => {
      const keys = Object.keys(obj || {});
      return (
        keys.find((k) => /^car\s*#$/i.test(k)) ||
        keys.find((k) => /^car#$/i.test(k)) ||
        keys.find((k) => /^number$/i.test(k)) ||
        keys.find((k) => /^#$/) ||
        null
      );
    };

    const nameCol =
      Object.keys(arr[0] || {}).find((k) => /^driver\b|^name$/i.test(k)) ||
      "Driver";
    const numCol = findNumCol(arr[0]) || "Car #";

    for (const r of arr) {
      const nm = keynorm(r?.[nameCol]);
      const nRaw = r?.[numCol];
      const n = Number(String(nRaw ?? "").replace(/[^\d.]/g, ""));
      if (nm && Number.isFinite(n)) map.set(nm, Math.round(n));
    }
    return map;
  }, [driverDataRaw]);

  /* Driver -> practice average map (prefer "Average", 1 decimal) */
  const practiceAvgMap = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(practiceRaw)
      ? practiceRaw
      : Array.isArray(practiceRaw?.rows)
      ? practiceRaw.rows
      : Array.isArray(practiceRaw?.data)
      ? practiceRaw.data
      : [];
    if (!arr.length) return m;

    const nameCol =
      Object.keys(arr[0] || {}).find((k) => /^driver\b|^name$/i.test(k)) ||
      "Driver";

    // Prefer "Average" -> "Avg" -> "Lap Avg"
    const avgCol =
      Object.keys(arr[0] || {}).find((k) => /^average$/i.test(k)) ||
      Object.keys(arr[0] || {}).find((k) => /^avg$/i.test(k)) ||
      Object.keys(arr[0] || {}).find((k) => /lap\s*avg/i.test(k)) ||
      "Average";

    for (const r of arr) {
      const nm = keynorm(r?.[nameCol]);
      const raw = r?.[avgCol];
      const n = Number(String(raw ?? "").replace(/[^\d.-]/g, ""));
      if (nm && Number.isFinite(n)) m.set(nm, n.toFixed(1)); // 1 decimal
    }
    return m;
  }, [practiceRaw]);

  /* --- table visuals / controls --- */
  const padCell = "px-2 py-1";
  const textSz = "text-xs";
  const headerWrap = "whitespace-normal break-words leading-tight";
  const cellWrap = "whitespace-normal break-words";

  const [theme, setTheme] = useState("dark"); // "dark" | "light"
  const isDark = theme === "dark";

  const [view, setView] = useState("projections"); // projections | compare | heatmap | charts

  const [heatSite, setHeatSite] = useState("DK"); // DK | FD (Heat Map tab)
  const [startPosCol, setStartPosCol] = useState(null);
  const autoStartPosCol = useMemo(() => findStartPosCol(rows[0] || {}), [rows]);
  // Default Start Pos selector to the detected qualifying/grid column (prefer "Qual" when present)
  useEffect(() => {
    if (startPosCol) return;
    if (!rows.length) return;
    const keys = Object.keys(rows[0] || {});
    const qual =
      keys.find((k) => /^qual$/i.test(String(k).trim())) ||
      keys.find((k) => /^qual(ifying)?\s*(pos|position)$/i.test(String(k).trim())) ||
      null;
    const pick = qual || autoStartPosCol;
    if (pick) setStartPosCol(pick);
  }, [rows, autoStartPosCol, startPosCol]);
  const activeStartPosCol = startPosCol || autoStartPosCol;


  const [brand, setBrand] = useState("Both");

  // Heat map site: follow the global DK/FD toggle when it is not "Both"
  const heatEffectiveSite = brand === "Both" ? heatSite : brand;
  const baseCols = useMemo(() => {
    const details = showDetailed ? detailColumns.filter((c) => detailGroup === "All details" || c.group === detailGroup).map((c) => c.label) : [];
    return [...allColsRaw, ...details].filter((c) => brand === "Both" || colBrand(c) === "NEUTRAL" || colBrand(c) === brand);
  }, [allColsRaw, brand, showDetailed, detailColumns, detailGroup]);

  useEffect(() => {
    if (brand !== "Both" && /^(DK|FD) points$/.test(detailGroup) && detailGroup !== `${brand} points`) setDetailGroup("All details");
  }, [brand, detailGroup]);
  useEffect(() => {
    setDetailGroup("All details");
    setSort({ col: "", dir: "asc" });
  }, [SOURCE]);

  const [visibleCols, setVisibleCols] = useState([]);
  useEffect(() => {
    if (baseCols.length) setVisibleCols(baseCols.map(() => true));
  }, [baseCols]);

  const visibleColNames = baseCols.filter((_, i) => visibleCols[i]);

  const [q, setQ] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");

  // Team and manufacturer may live in projections.json or in driver data.json.
  const driverDetails = useMemo(() => {
    const details = new Map();
    const dataRows = Array.isArray(driverDataRaw)
      ? driverDataRaw
      : Array.isArray(driverDataRaw?.rows)
      ? driverDataRaw.rows
      : Array.isArray(driverDataRaw?.data)
      ? driverDataRaw.data
      : Array.isArray(driverDataRaw?.drivers)
      ? driverDataRaw.drivers
      : [];
    const field = (r, pattern) => {
      const key = Object.keys(r || {}).find((k) =>
        pattern.test(k.toLowerCase().replace(/[^a-z0-9]/g, ""))
      );
      return String(key ? r[key] ?? "" : "").trim();
    };
    for (const r of dataRows) {
      const name = field(r, /^(driver|drivername|name)$/);
      if (name) details.set(keynorm(name), {
        team: field(r, /^(team|teamname|organization|org)$/),
        manufacturer: field(r, /^(manufacturer|make|mfr|carmake)$/),
      });
    }
    const byRow = new Map();
    for (const r of rows) {
      const extra = details.get(keynorm(r?.[driverKey])) || {};
      byRow.set(r, {
        team: field(r, /^(team|teamname|organization|org)$/) || extra.team || "",
        manufacturer: field(r, /^(manufacturer|make|mfr|carmake)$/) || extra.manufacturer || "",
      });
    }
    return byRow;
  }, [rows, driverDataRaw, driverKey]);

  const teamOptions = useMemo(() =>
    [...new Set([...driverDetails.values()].map((d) => d.team).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)), [driverDetails]);
  const manufacturerOptions = useMemo(() =>
    [...new Set([...driverDetails.values()].map((d) => d.manufacturer).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)), [driverDetails]);

  useEffect(() => {
    setSelectedTeam("");
    setSelectedManufacturer("");
  }, [seriesKey]);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      const details = driverDetails.get(r);
      if (selectedTeam && details?.team !== selectedTeam) return false;
      if (selectedManufacturer && details?.manufacturer !== selectedManufacturer) return false;
      return !needle || allColsRaw.some((c) =>
        String(r?.[c] ?? "").toLowerCase().includes(needle)
      );
    });
  }, [rows, allColsRaw, q, driverDetails, selectedTeam, selectedManufacturer]);

  const heatStats = useMemo(() => {
    const stats = {};
    if (!filteredRows.length) return stats;
    for (const c of visibleColNames) {
      const dir = dirForCol(c);
      if (!dir) continue;
      let min = Infinity,
        max = -Infinity;
      for (const r of filteredRows) {
        if (DETAIL_BY_LABEL[c] && detailNumber(r?.[c]) === null) continue;
        const n = num(r?.[c]);
        if (!Number.isFinite(n)) continue;
        if (n < min) min = n;
        if (n > max) max = n;
      }
      if (min !== Infinity && max !== -Infinity) stats[c] = { min, max, dir };
    }
    return stats;
  }, [filteredRows, visibleColNames]);

  const [sort, setSort] = useState({ col: "", dir: "asc" });
  const sortedRows = useMemo(() => {
    if (!sort.col) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      if (DETAIL_BY_LABEL[sort.col]) {
        const av = detailNumber(a[sort.col]), bv = detailNumber(b[sort.col]);
        if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
      }
      return cmp(a?.[sort.col], b?.[sort.col], sort.dir);
    });
  }, [filteredRows, sort]);

  const heatRows = useMemo(() => {
    // Heat Map should always display drivers ordered by starting position (1..40)
    // If no start position is available yet, fall back to current sortedRows behavior.
    if (!activeStartPosCol) return sortedRows;
    const getStart = (r) => {
      const v = r?.[activeStartPosCol];
      const n = Number(v);
      return Number.isFinite(n) ? n : Infinity;
    };
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const sa = getStart(a);
      const sb = getStart(b);
      if (sa !== sb) return sa - sb;
      const da = String(a?.[driverKey] ?? "").toLowerCase();
      const db = String(b?.[driverKey] ?? "").toLowerCase();
      return da.localeCompare(db);
    });
    return arr;
  }, [filteredRows, sortedRows, activeStartPosCol, driverKey]);

  const siteCols = useMemo(() => extractSiteCols(allColsRaw), [allColsRaw]);

  function PointDistModal({ open, onClose, isDark, site, driverName, bins, proj }) {
    const [isMobile, setIsMobile] = useState(
      typeof window !== "undefined" ? window.innerWidth < 640 : false
    );

    useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const handleResize = () => setIsMobile(window.innerWidth < 640);
      handleResize();

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    const chartData = Array.isArray(bins)
      ? bins.map((b) => {
          const a = Number(b?.bin_start);
          const z = Number(b?.bin_end);
          const p = Number(b?.prob);
          return {
            label: Number.isFinite(a) && Number.isFinite(z) ? `${a}–${z}` : "",
            bin_start: a,
            bin_end: z,
            mid: Number.isFinite(a) && Number.isFinite(z) ? (a + z) / 2 : NaN,
            probPct: Number.isFinite(p) ? p * 100 : 0,
          };
        })
      : [];

    const hasData = chartData.length > 0;

    const xDomain = useMemo(() => {
      const mids = chartData
        .map((d) => d.mid)
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      if (!mids.length) return [0, 1];
      if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];

      const firstGap = Math.abs(mids[1] - mids[0]) || 1;
      const lastGap =
        Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;

      return [
        mids[0] - firstGap / 2,
        mids[mids.length - 1] + lastGap / 2,
      ];
    }, [chartData]);

    const xTicks = useMemo(() => {
      if (!chartData.length) return [];

      return chartData
        .filter((_, i) => (isMobile ? i % 4 === 0 : i % 2 === 0))
        .map((d) => d.mid)
        .filter(Number.isFinite);
    }, [chartData, isMobile]);

    if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={[
          "relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto",
          isDark ? "border-slate-700/60 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-3 px-3 pt-4 sm:gap-4 sm:px-5 sm:pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                Fantasy Points Distribution
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="max-w-[190px] truncate text-base font-extrabold tracking-tight sm:max-w-none sm:text-lg">{driverName || "—"}</div>
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide",
                    site === "DK"
                      ? (isDark ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30" : "bg-emerald-50 text-emerald-800 border border-emerald-200")
                      : (isDark ? "bg-sky-500/15 text-sky-200 border border-sky-500/30" : "bg-sky-50 text-sky-800 border border-sky-200"),
                  ].join(" ")}
                >
                  {site}
                </span>
                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{oneDecMax(proj)}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <button
              className={[
                "shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-semibold sm:px-3 sm:py-2 sm:text-sm",
                isDark ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900" : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>

          <div className="px-2 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
            {!hasData ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>
                No distribution data found for this driver yet.
              </div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 18,
                      left: 8,
                      bottom: isMobile ? 42 : 18,
                    }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid
                      stroke={isDark ? "#334155" : "#e5e7eb"}
                      strokeDasharray="3 3"
                    />
                      <XAxis
                        dataKey="mid"
                        type="number"
                        domain={xDomain}
                        ticks={xTicks}
                        tickFormatter={(v) => `${Math.round(v)}`}
                        tick={{ fontSize: isMobile ? 11 : 12, fill: isDark ? "#cbd5f5" : "#334155" }}
                        axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                        tickLine={false}
                        angle={isMobile ? -35 : 0}
                        textAnchor={isMobile ? "end" : "middle"}
                        height={isMobile ? 52 : 30}
                      />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: isMobile ? 10 : 11, fill: isDark ? "#cbd5f5" : "#334155" }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      width={isMobile ? 38 : 60}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(_, payload) => {
                        const p = payload?.[0]?.payload;
                        return p?.label ? `Bin ${p.label}` : "";
                      }}
                    />
                      {Number.isFinite(proj) && (
                        <ReferenceLine
                          x={proj}
                          stroke="#38bdf8"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                        />
                      )}
                    <Bar
                      dataKey="probPct"
                      radius={[6, 6, 0, 0]}
                      fill="#7dd3fc"
                      stroke="#38bdf8"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

  const rowByDriver = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = keynorm(r?.[driverKey]);
      if (k) m.set(k, r);
    }
    return m;
  }, [rows, driverKey]);

  const iconForRow = (r) => {
    const name = String(r?.[driverKey] ?? "");
    const nm = keynorm(name);
    const carNum = driverNumMap.get(nm);
    return Number.isFinite(carNum) ? `${NUM_LOGO_BASE}/${carNum}.png` : "";
  };

  const resetUI = () => {
    setShowDetailed(false);
    setDetailGroup("All details");
    setQ("");
    setSelectedTeam("");
    setSelectedManufacturer("");
    setSort({ col: "", dir: "asc" });
    setVisibleCols(baseCols.map(() => true));
  };

  /* ----------------------------- compare state ----------------------------- */
  const [compareMode, setCompareMode] = useState("A"); // A | B (which bucket clicks add to)
  const [groupA, setGroupA] = useState(() => new Set());
  const [groupB, setGroupB] = useState(() => new Set());

  const toggleInSet = (setter, setObj, key) => {
    const next = new Set(setObj);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  const groupARows = useMemo(() => {
    if (!groupA.size) return [];
    return rows.filter((r) => groupA.has(keynorm(r?.[driverKey])));
  }, [rows, groupA, driverKey]);
  const groupBRows = useMemo(() => {
    if (!groupB.size) return [];
    return rows.filter((r) => groupB.has(keynorm(r?.[driverKey])));
  }, [rows, groupB, driverKey]);

  const groupASum = useMemo(
    () => summarizeGroup(groupARows, allColsRaw, driverKey),
    [groupARows, allColsRaw, driverKey]
  );
  const groupBSum = useMemo(
    () => summarizeGroup(groupBRows, allColsRaw, driverKey),
    [groupBRows, allColsRaw, driverKey]
  );

  // If the slate updates, reset old selections (keeps things sane)
  useEffect(() => {
    setGroupA(new Set());
    setGroupB(new Set());
  }, [SOURCE]);



  const exportCSV = () => {
    const cols = visibleColNames.length ? visibleColNames : baseCols;
    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of sortedRows) {
      lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${CSV_PREFIX}_${brand.toLowerCase()}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const colWidthCh = useMemo(() => {
    const widths = {};
    if (!rows.length) return widths;
    const sample = rows.slice(0, 200);
    for (const c of baseCols) {
      let maxLen = 0;
      const headerLen = String(c).length;
      for (const r of sample) {
        const s = String(detailValue(c, r?.[c]));
        if (s.length > maxLen) maxLen = s.length;
      }
      const numeric = Boolean(DETAIL_BY_LABEL[c]) || looksNumericCol(c);
      if (isDriverCol(c)) widths[c] = clamp(Math.max(maxLen, 14), 14, 24);
      else if (numeric)
        widths[c] = clamp(Math.max(maxLen + 2, headerLen + 1, 7), 7, 14);
      else widths[c] = clamp(Math.max(maxLen, Math.min(headerLen, 12)), 10, 16);
    }
    return widths;
  }, [rows, baseCols]);

  /* Modal state */
  const [showDriver, setShowDriver] = useState(null);
  const [showDriverNum, setShowDriverNum] = useState();

  /* Point distribution modal state */
  const [pointDistOpen, setPointDistOpen] = useState(false);
  const [pointDistSite, setPointDistSite] = useState("DK"); // "DK" | "FD"
  const [pointDistDriverKey, setPointDistDriverKey] = useState("");
  const [pointDistDriverName, setPointDistDriverName] = useState("");


  const pointDists = pointDistsRaw && typeof pointDistsRaw === "object" ? pointDistsRaw : null;

const pointDistsIndex = useMemo(() => {
  if (!pointDists) return null;

  const build = (obj) => {
    const out = {};
    if (!obj || typeof obj !== "object") return out;
    for (const [name, bins] of Object.entries(obj)) {
      const k = keynorm(name);
      if (k) out[k] = bins;
    }
    return out;
  };

  return {
    dk: build(pointDists.dk),
    fd: build(pointDists.fd),
  };
}, [pointDists]);


  const openPointDist = (row, site) => {
    const name = row?.[driverKey] ?? row?.Driver ?? "";
    const key = keynorm(name);
    setPointDistSite(site);
    setPointDistDriverKey(key);
    setPointDistDriverName(String(name || "").trim());
    setPointDistOpen(true);
  };

  const closePointDist = () => setPointDistOpen(false);

  /* theme-based outer + card classes */
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  // Fully opaque header (no transparency)
  const headerBgClass = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-white text-slate-800";

  const headerBorderClass = isDark ? "border-slate-800/80" : "border-gray-200";

  // Opaque zebra rows
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";

  return (
    <div className={outerClass}>
      {/* Make sure this page never beats global overlays (mobile menu/drawers) */}
      <div className="relative z-0">
        <div className="w-full px-4 md:px-6 py-5">
          {/* Header */}
          <header className="mb-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-xl font-bold tracking-tight">{TITLE}</h1>
                <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedAt ? fmtUpdated(updatedAt) : "—"}</span>
              </div>
              <div
                role="group"
                aria-label="Color theme"
                className={
                  "inline-flex shrink-0 items-center rounded-full p-1 ring-1 " +
                  (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
                }
              >
                <button type="button" aria-pressed={isDark} onClick={() => setTheme("dark")}
                  className={"rounded-full px-3 py-1 text-[11px] font-medium transition " +
                    (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")}>Dark</button>
                <button type="button" aria-pressed={!isDark} onClick={() => setTheme("light")}
                  className={"rounded-full px-3 py-1 text-[11px] font-medium transition " +
                    (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")}>Light</button>
              </div>            </div>

            {/* Series navigation uses the same underline treatment as Optimizer / Late Swap. */}
            <div className={cardClass + " flex flex-wrap items-end justify-between gap-x-6 gap-y-3 px-3 py-3"}>
              <nav aria-label="NASCAR series" className={"flex flex-wrap items-center gap-5 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
                {[
                  ["cup", "Cup"],
                  ["xfinity", "O'Reilly"],
                  ["trucks", "Trucks"],
                ].map(([k, label]) => (
                  <a
                    key={k}
                    href={`/nascar/${k === "xfinity" ? "oreilly" : k}/projections`}
                    aria-current={seriesKey === k ? "page" : undefined}
                    className={
                      "relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-xs font-semibold transition-colors " +
                      (seriesKey === k
                        ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                        : isDark
                        ? "text-slate-400 hover:text-slate-100"
                        : "text-slate-500 hover:text-slate-900")
                    }
                  >
                    {label}
                  </a>
                ))}
              </nav>

              <div className="min-w-0 max-w-full">
                <SegTabs
                  isDark={isDark}
                  value={view}
                  onChange={setView}
                  tabs={[
                    { key: "projections", label: "Projections" },
                    { key: "compare", label: "Projection Comparison" },
                    { key: "heatmap", label: "Heat Map" },
                    { key: "charts", label: "Charts" },
                  ]}
                />
              </div>
            </div>

            {SHOW_SOURCE && <div className="text-xs text-slate-500"><code>{SOURCE}</code></div>}

            {/* Search, filters, site, actions, and theme */}
            <div className={cardClass + " flex flex-wrap items-center gap-3 px-3 py-3"}>
              <input
                type="search"
                aria-label="Search drivers"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={view === "charts" ? "Search drivers to add…" : "Search drivers…"}
                className={
                  "h-8 w-full min-w-0 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 sm:flex-[1_1_220px] xl:flex-[1_1_180px] " +
                  (isDark
                    ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-500"
                    : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400")
                }
              />

              <select
                aria-label="Filter by team"
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className={
                  "h-8 min-w-0 w-[calc(50%-0.375rem)] flex-none rounded-md border px-3 text-xs sm:w-auto sm:flex-[0_1_180px] xl:w-[170px] xl:flex-none " +
                  (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")
                }
              >
                <option value="">All teams</option>
                {teamOptions.map((team) => <option key={team} value={team}>{team}</option>)}
              </select>

              <select
                aria-label="Filter by manufacturer"
                value={selectedManufacturer}
                onChange={(e) => setSelectedManufacturer(e.target.value)}
                className={
                  "h-8 min-w-0 w-[calc(50%-0.375rem)] flex-none rounded-md border px-3 text-xs sm:w-auto sm:flex-[0_1_180px] xl:w-[185px] xl:flex-none " +
                  (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")
                }
              >
                <option value="">All manufacturers</option>
                {manufacturerOptions.map((make) => <option key={make} value={make}>{make}</option>)}
              </select>

              <div
                role="tablist"
                aria-label="Fantasy site"
                className={
                  "inline-flex shrink-0 items-center border-b " +
                  (isDark ? "border-slate-800" : "border-slate-200")
                }
              >
                {[
                  { key: "DK", label: <><img src={LOGO_DK} alt="" className="h-4 w-4 shrink-0 object-contain" />DK</> },
                  { key: "FD", label: <><img src={LOGO_FD} alt="" className="h-4 w-4 shrink-0 object-contain" />FD</> },
                  { key: "Both", label: "Both" },
                ].map((b) => {
                  const active = brand === b.key;
                  return (
                    <button
                      key={b.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setBrand(b.key)}
                      className={
                        "inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-xs font-semibold transition " +
                        (active
                          ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                          : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                      }
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {view === "projections" ? (
                  <>
                    <label className="inline-flex h-8 cursor-pointer items-center gap-2 whitespace-nowrap px-2 text-xs font-medium">
                      <input type="checkbox" checked={showDetailed} onChange={(e) => { setShowDetailed(e.target.checked); setSort({ col: "", dir: "asc" }); }} className="h-4 w-4 accent-sky-500" />
                      Show detailed
                    </label>
                    {showDetailed && <select aria-label="Detailed stat category" value={detailGroup} onChange={(e) => { setDetailGroup(e.target.value); setSort({ col: "", dir: "asc" }); }} className={"h-8 rounded-md border px-2 text-xs " + (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")}>
                      {["All details", ...new Set(detailColumns.filter((c) => brand === "Both" || colBrand(c.label) === "NEUTRAL" || colBrand(c.label) === brand).map((c) => c.group))].map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>}
                    <button
                      type="button"
                      className={
                        "h-8 rounded-md border px-3 text-xs transition " +
                        (isDark ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-900" : "border-slate-300 bg-white text-slate-900 hover:bg-slate-50")
                      }
                      onClick={resetUI}
                      title="Clear search and filters, sorting, and show all columns"
                    >Reset</button>
                    <button
                      type="button"
                      className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 shadow-sm transition hover:bg-sky-400"
                      onClick={exportCSV}
                      title="Export visible columns & filtered/sorted rows"
                    >Export CSV</button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={
                      "h-8 rounded-md border px-3 text-xs transition " +
                      (isDark ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-900" : "border-slate-300 bg-white text-slate-900 hover:bg-slate-50")
                    }
                    onClick={() => {
                      setQ("");
                      setSelectedTeam("");
                      setSelectedManufacturer("");
                    }}
                    title="Clear search and filters"
                  >Clear</button>
                )}
              </div>


            </div>
          </header>

{/* Loading / error */}
{loading && <div className="text-sm text-slate-400">Loading…</div>}
{err && (
  <div className="text-sm text-red-400">
    Failed to load: {String(err)}
  </div>
)}

          {/* Projections table */}
          {!loading && !err && view === "projections" && (
            <section aria-label="Driver projections" className={cardClass}>
              <div className={"border-b px-4 py-3 text-sm font-semibold " + headerBorderClass}>Driver Projections</div>
              {showDetailed && <div role="status" className={"border-b px-4 py-3 text-xs leading-relaxed " + headerBorderClass + (isDark ? " text-slate-400" : " text-slate-600")}>
                {simSummaryLoading ? "Loading simulation details…" : simSummaryError ? "Simulation details unavailable. Check that sim_summary.json is in this series’ latest folder." : !detailColumns.length ? "No simulation detail fields available for this series." : <>
                  Floor = P10 · Median = P50 · Ceiling = P90 · P95/P99 = upper-tail outcomes, not maximums. Finish: lower is better; P10 is the best-side outcome. Dominator chances measure race laps led (25%+ / 50%+), not fantasy points. LL = laps led · FL = fastest laps · PD = place differential. Missing values appear as —. Scroll horizontally for more stats.
                  {rows.some((r) => !summaryByDriver.has(keynorm(r[driverKey]))) && " Some drivers have no matching simulation summary."}
                </>}
              </div>}
              <div className="overflow-auto max-h-[70vh] rounded-b-2xl">
              <table
                className={`w-full table-fixed border-separate ${textSz}`}
                style={{ borderSpacing: 0, minWidth: showDetailed ? `${visibleColNames.reduce((sum, c) => sum + (colWidthCh[c] ?? 10), 0)}ch` : undefined }}
              >
                <colgroup>
                  {visibleColNames.map((c) => (
                    <col key={c} style={{ width: `${colWidthCh[c] ?? 10}ch` }} />
                  ))}
                </colgroup>

                <thead>
                  <tr>
                    {visibleColNames.map((c) => {
                      const active = sort.col === c;
                      const arrow = active
                        ? sort.dir === "asc"
                          ? "▲"
                          : "▼"
                        : "▲";
                      const w = colWidthCh[c] ?? 10;

                      return (
                        <th
                          key={c}
                          onClick={() =>
                            setSort((s) =>
                              s.col === c
                                ? {
                                    col: c,
                                    dir: s.dir === "asc" ? "desc" : "asc",
                                  }
                                : { col: c, dir: "asc" }
                            )
                          }
                          title={DETAIL_BY_LABEL[c] ? `${c} — source: ${DETAIL_BY_LABEL[c].field}${DETAIL_BY_LABEL[c].probability ? "; displayed as a percentage" : ""}` : c}
                          className={[
                            "sticky top-0",
                            headerBgClass,
                            "font-semibold border-b",
                            headerBorderClass,
                            "px-2 py-2",
                            textSz,
                            "cursor-pointer select-none text-center",
                            "overflow-hidden",
                            // LOWER Z-INDEX so mobile menus/drawers can sit above
                            isDriverCol(c)
                              ? "left-0 z-20 shadow-[6px_0_12px_rgba(0,0,0,0.35)]"
                              : "z-10",
                          ].join(" ")}
                          style={{ maxWidth: `${w}ch` }}
                        >
                          <div
                            className={[
                              "flex items-center gap-1 justify-center w-full",
                              headerWrap,
                            ].join(" ")}
                          >
                            {c}{" "}
                            <span className="opacity-60 text-[10px]">
                              {arrow}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {sortedRows.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;
                    const hover = "hover:bg-sky-900/40 transition-colors";
                    return (
                      <tr key={i} className={[rowBg, hover].join(" ")}>
                        {visibleColNames.map((c) => {
                          const isDriver = isDriverCol(c);
                          const v = r?.[c];

                          const numericCol =
                            !isDriver && (looksNumericCol(c) || isNumeric(v));

                          const textColor =
                            !isDriver && heatStats[c]
                              ? heatTextColor(
                                  heatStats[c].min,
                                  heatStats[c].max,
                                  num(v),
                                  heatStats[c].dir,
                                  theme
                                )
                              : null;

                          const driverName = isDriver ? String(v ?? "") : "";
                          const nm = isDriver ? keynorm(driverName) : "";
                          const carNum =
                            isDriver && driverName
                              ? driverNumMap.get(nm)
                              : undefined;
                          const iconSrc =
                            isDriver && Number.isFinite(carNum)
                              ? `${NUM_LOGO_BASE}/${carNum}.png`
                              : "";

                          return (
                            <td
                              key={c}
                              className={[
                                "border-b border-slate-800/80",
                                padCell,
                                textSz,
                                isDriver ? "text-left" : "text-center",
                                "tabular-nums",
                                isDriver
                                  ? `sticky left-0 z-10 ${rowBg} border-r border-slate-800/80 shadow-[6px_0_12px_rgba(0,0,0,0.25)]`
                                  : "",
                              ].join(" ")}
                              style={{
                                maxWidth: `${colWidthCh[c] ?? 10}ch`,
                              }}
                              title={String(v ?? "")}
                            >
                              {isDriver ? (
                                <div className="flex items-center gap-2">
                                  {iconSrc ? (
                                    // eslint-disable-next-line jsx-a11y/alt-text
                                    <img
                                      src={iconSrc}
                                      className={`${DRIVER_ICON_SIZE} object-contain`}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : null}
                                  <button
                                    className="group relative min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded-md px-1 py-0.5
                                               hover:bg-slate-800/40 hover:underline decoration-2 decoration-slate-300 transition"
                                    onClick={() => {
                                      setShowDriver(r);
                                      setShowDriverNum(
                                        Number.isFinite(carNum)
                                          ? carNum
                                          : undefined
                                      );
                                    }}
                                  >
                                    {driverName}
                                    <HoverHint text="Open driver card" />
                                  </button>
                                </div>
                              ) : (
                              <div
                                className={
                                  numericCol
                                    ? "w-full whitespace-nowrap"
                                    : ["w-full", cellWrap].join(" ")
                                }
                              >
                                {c === siteCols?.dk?.proj || c === siteCols?.fd?.proj ? (
                                  <button
                                    className={[
                                      "group relative w-full text-center font-semibold rounded-md px-1 py-0.5",
                                      "underline underline-offset-4 decoration-sky-400/70",
                                      "hover:decoration-sky-300 hover:bg-sky-500/10",
                                      "transition cursor-pointer",
                                    ].join(" ")}
                                    style={textColor ? { color: textColor } : undefined}
                                    onClick={() => openPointDist(r, c === siteCols?.dk?.proj ? "DK" : "FD")}
                                  >
                                    {v ?? ""}
                                    <HoverHint text="Click to open point distribution chart" />
                                  </button>
                                ) : (
                                  <span
                                    style={textColor && (!DETAIL_BY_LABEL[c] || detailNumber(v) !== null) ? { color: textColor, fontWeight: 600 } : undefined}
                                  >
                                    {detailValue(c, v)}
                                  </span>
                                )}
                              </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}

                  {!sortedRows.length && (
                    <tr>
                      <td
                        className={`px-2 py-1 ${textSz} text-slate-500`}
                        colSpan={visibleColNames.length}
                      >
                        No rows.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </section>
          )}

          
{/* Heat Map */}
{!loading && !err && view === "heatmap" && (
  <div className={cardClass + " p-4"}>
    <div className="flex items-start md:items-center justify-between gap-3 flex-wrap mb-3">
      <div>
        <div className="text-sm font-extrabold">Heat Map</div>
        <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-600"}>
          Finish points + position differential. Row coloring scales from <span className="font-semibold">best (green)</span> to <span className="font-semibold">worst (red)</span> per driver.
        </div>
      </div>

      <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap">
        {/* DK / FD toggle inside Heat Map tab */}
        <div
          role="tablist"
          className={
            "inline-flex items-center rounded-xl p-1 border shadow-inner " +
            (isDark ? "bg-slate-900/80 border-slate-700/80" : "bg-white border-slate-200")
          }
        >
          {[
            {
              key: "DK",
              label: (
                <>
                  <img src={LOGO_DK} alt="DK" className="h-4 w-4 shrink-0 object-contain" />
                  DK
                </>
              ),
            },
            {
              key: "FD",
              label: (
                <>
                  <img src={LOGO_FD} alt="FD" className="h-4 w-4 shrink-0 object-contain" />
                  FD
                </>
              ),
            },
          ].map((b) => {
            const active = heatEffectiveSite === b.key;
            return (
              <button
                key={b.key}
                role="tab"
                aria-selected={active}
                onClick={() => { setHeatSite(b.key); setBrand(b.key); }}
                className={[
                  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition min-w-[84px]",
                  active
                    ? "bg-sky-500 text-slate-950 shadow border border-slate-900"
                    : isDark
                    ? "text-slate-200 hover:text-white"
                    : "text-slate-700 hover:text-slate-900",
                ].join(" ")}
              >
                {b.label}
              </button>
            );
          })}
        </div>

        {/* Start position column selector (auto-detects, but can override) */}
        <div className="flex items-center gap-2">
          <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>
            Start Pos
          </div>
          <select
            value={activeStartPosCol || ""}
            onChange={(e) => setStartPosCol(e.target.value || null)}
            className={
              "h-8 rounded-full border px-2 text-xs focus:outline-none " +
              (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
            }
            title="Select the column that contains the driver's starting position / qualifying grid spot."
          >
            <option value="">(none)</option>
            {allColsRaw
              .filter((c) =>
                /start|starting|grid|qual|position|pos\b/i.test(String(c))
              )
              .map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
          </select>
        </div>
      </div>
    </div>

    {!activeStartPosCol && (
      <div
        className={
          "mb-3 rounded-xl border px-3 py-2 text-xs " +
          (isDark ? "bg-amber-950/30 border-amber-800/60 text-amber-200" : "bg-amber-50 border-amber-200 text-amber-900")
        }
      >
        No start position column detected/selected yet. Once your qualifying grid is in the data, choose the correct <span className="font-semibold">Start Pos</span> column to populate the heat map.
      </div>
    )}

    {/* Single scroll container so sticky header/first column work reliably */}
    <div className="overflow-auto max-h-[80vh] relative">
      <table className="min-w-full text-xs">
        <thead className={isDark ? "bg-slate-950" : "bg-white"}>
          <tr>
            <th
              className={
                padCell +
                " text-left font-extrabold w-[220px] max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap " +
                headerWrap +
                " sticky top-0 left-0 z-30 " +
                (isDark ? "bg-slate-950 " : "bg-white ") +
                headerBorderClass +
                " border-b"
              }
            >
              Driver
            </th>
            <th className={padCell + " sticky top-0 z-20 text-center font-extrabold " + headerWrap + " " + headerBgClass + " border-b " + headerBorderClass}>
              Start
            </th>
            {Array.from({ length: 40 }, (_, i) => i + 1).map((pos) => (
              <th
                key={pos}
                className={padCell + " sticky top-0 z-10 text-center font-extrabold " + headerWrap + " " + headerBgClass + " border-b " + headerBorderClass}
                title={`Finish ${pos}`}
              >
                {pos}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {heatRows.map((r, idx) => {
            const name = String(r?.[driverKey] ?? "");
            const nm = keynorm(name);
            const startRaw = activeStartPosCol ? r?.[activeStartPosCol] : null;
            const startPos = Number(String(startRaw ?? "").replace(/[^\d.-]/g, ""));
            const hasStart = Number.isFinite(startPos) && startPos > 0;

            const mult = heatEffectiveSite === "DK" ? 1 : 0.5;
            const finishPts = heatEffectiveSite === "DK" ? DK_FINISH_PTS : FD_FINISH_PTS;

            const vals = Array.from({ length: 40 }, (_, i) => {
              const fin = i + 1;
              const fp = finishPts[fin];
              if (!hasStart || !Number.isFinite(fp)) return NaN;
              return fp + (startPos - fin) * mult;
            });

            let rowMin = Infinity;
            let rowMax = -Infinity;
            for (const v of vals) {
              if (!Number.isFinite(v)) continue;
              if (v < rowMin) rowMin = v;
              if (v > rowMax) rowMax = v;
            }
            if (rowMin === Infinity || rowMax === -Infinity) {
              rowMin = NaN;
              rowMax = NaN;
            }

            const zebra = idx % 2 ? rowEvenClass : rowOddClass;

            return (
              <tr key={nm || idx} className={zebra}>
                <td
                  className={
                    padCell +
                    " font-semibold sticky left-0 z-20 " +
                    zebra +
                    " w-[220px] max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap border-b " +
                    headerBorderClass
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowDriver(name);
                      setShowDriverNum(driverNumMap.get(nm));
                    }}
                    className="hover:underline"
                    title="Open driver card"
                  >
                    {name || "—"}
                  </button>
                </td>

                <td className={padCell + " text-center tabular-nums border-b " + headerBorderClass}>
                  {hasStart ? Math.round(startPos) : "—"}
                </td>

                {vals.map((v, j) => {
                  const bg = heatTextColor(rowMin, rowMax, v, "higher", theme);
                  return (
                    <td
                      key={j}
                      className={padCell + " text-center tabular-nums border-b " + headerBorderClass}
                      style={
                            bg
                              ? {
                                  backgroundColor: bg,
                                  color: "#000000",
                                }
                              : { color: "#000000" }
                          }
                    >
                      {Number.isFinite(v) ? oneDec(v) : "—"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
)}

{/* Projection Comparison */}
          {!loading && !err && view === "compare" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: Group A */}
              <div className={cardClass + " p-4"}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-extrabold">Group A</div>
                    <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                      Click drivers to add/remove (mode: A)
                    </div>
                  </div>
                  <button
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-semibold border " +
                      (compareMode === "A"
                        ? "bg-sky-500 text-slate-950 border-slate-900"
                        : isDark
                        ? "bg-slate-950 text-slate-100 border-slate-800 hover:bg-slate-900"
                        : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50")
                    }
                    onClick={() => setCompareMode("A")}
                  >
                    Active
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <MetricChip label="Drivers" value={String(groupASum.dk.count)} isDark={isDark} />
                  <MetricChip label="DK Salary" value={money(groupASum.dk.sal)} isDark={isDark} />
                  <MetricChip label="DK Proj" value={oneDec(groupASum.dk.proj)} isDark={isDark} />
                  <MetricChip label="DK pOWN" value={oneDec(groupASum.dk.pown)} isDark={isDark} />
                  <MetricChip label="FD Salary" value={money(groupASum.fd.sal)} isDark={isDark} />
                  <MetricChip label="FD Proj" value={oneDec(groupASum.fd.proj)} isDark={isDark} />
                </div>

                <div className="mt-4">
                  <div className={isDark ? "text-xs text-slate-400 mb-2" : "text-xs text-slate-500 mb-2"}>
                    Selected ({groupA.size})
                  </div>
                  <div className="space-y-2 max-h-[38vh] overflow-auto pr-1">
                    {[...groupA]
                      .map((k) => rowByDriver.get(k))
                      .filter(Boolean)
                      .map((r) => (
                        <DriverPill
                          key={keynorm(r?.[driverKey])}
                          name={String(r?.[driverKey] ?? "")}
                          iconSrc={iconForRow(r)}
                          right={
                            siteCols?.dk?.proj
                              ? `DK ${oneDec(num(r?.[siteCols.dk.proj]))}`
                              : null
                          }
                          isDark={isDark}
                          onClick={() => {
                            const k = keynorm(r?.[driverKey]);
                            toggleInSet(setGroupA, groupA, k);
                          }}
                        />
                      ))}
                    {!groupA.size && (
                      <div className={isDark ? "text-xs text-slate-500" : "text-xs text-slate-600"}>
                        No drivers selected.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Middle: picker */}
              <div className={cardClass + " p-4"}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-extrabold">Driver Pool</div>
                    <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                      Click a driver to toggle into the active group.
                    </div>
                  </div>
                  <button
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-semibold border " +
                      (isDark
                        ? "bg-slate-950 text-slate-100 border-slate-800 hover:bg-slate-900"
                        : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50")
                    }
                    onClick={() => {
                      setGroupA(new Set());
                      setGroupB(new Set());
                    }}
                    title="Clear both groups"
                  >
                    Clear all
                  </button>
                </div>

                <div className="mt-3">
                  <SegTabs
                    isDark={isDark}
                    value={compareMode}
                    onChange={setCompareMode}
                    tabs={[
                      { key: "A", label: "Add to A" },
                      { key: "B", label: "Add to B" },
                    ]}
                  />
                </div>

                <div className="mt-4 space-y-2 max-h-[56vh] overflow-auto pr-1">
                  {filteredRows
                    .slice()
                    .sort((a, b) => {
                      const col = siteCols?.dk?.proj || siteCols?.fd?.proj;
                      if (!col) return cmp(a?.[driverKey], b?.[driverKey], "asc");
                      return cmp(a?.[col], b?.[col], "desc");
                    })
                    .map((r) => {
                      const k = keynorm(r?.[driverKey]);
                      const inA = groupA.has(k);
                      const inB = groupB.has(k);

                      const qPosNum = activeStartPosCol ? num(r?.[activeStartPosCol]) : NaN;
                      const qLabel = Number.isFinite(qPosNum) ? `Q ${Math.trunc(qPosNum)}` : "Q -";

                      const right =
                        brand === "FD"
                          ? `FD ${money(num(r?.[siteCols.fd.sal]))} · ${oneDec(num(r?.[siteCols.fd.proj]))} · ${qLabel}`
                          : `DK ${money(num(r?.[siteCols.dk.sal]))} · ${oneDec(num(r?.[siteCols.dk.proj]))} · ${qLabel}`;

                      return (
                        <div key={k} className="relative">
                          <DriverPill
                            name={String(r?.[driverKey] ?? "")}
                            iconSrc={iconForRow(r)}
                            right={right}
                            isDark={isDark}
                            onClick={() => {
                              if (compareMode === "A") toggleInSet(setGroupA, groupA, k);
                              else toggleInSet(setGroupB, groupB, k);
                            }}
                          />

                          {/* BADGES (supports A + B at the same time) */}
                          {(inA || inB) ? (
                            <div className="absolute -top-1 -right-1 flex flex-col gap-1 items-end">
                              {inA ? (
                                <div
                                  className={
                                    "h-6 w-6 rounded-full text-[11px] font-extrabold flex items-center justify-center border shadow-sm " +
                                    "bg-sky-500 text-slate-950 border-slate-900"
                                  }
                                  title="In Group A"
                                >
                                  A
                                </div>
                              ) : null}

                              {inB ? (
                                <div
                                  className={
                                    "h-6 w-6 rounded-full text-[11px] font-extrabold flex items-center justify-center border shadow-sm " +
                                    "bg-emerald-500 text-slate-950 border-slate-900"
                                  }
                                  title="In Group B"
                                >
                                  B
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Right: Group B + diff */}
              <div className={cardClass + " p-4"}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-extrabold">Group B</div>
                    <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                      Click drivers to add/remove (mode: B)
                    </div>
                  </div>
                  <button
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-semibold border " +
                      (compareMode === "B"
                        ? "bg-emerald-500 text-slate-950 border-slate-900"
                        : isDark
                        ? "bg-slate-950 text-slate-100 border-slate-800 hover:bg-slate-900"
                        : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50")
                    }
                    onClick={() => setCompareMode("B")}
                  >
                    Active
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <MetricChip label="Drivers" value={String(groupBSum.dk.count)} isDark={isDark} />
                  <MetricChip label="DK Salary" value={money(groupBSum.dk.sal)} isDark={isDark} />
                  <MetricChip label="DK Proj" value={oneDec(groupBSum.dk.proj)} isDark={isDark} />
                  <MetricChip label="DK pOWN" value={oneDec(groupBSum.dk.pown)} isDark={isDark} />
                  <MetricChip label="FD Salary" value={money(groupBSum.fd.sal)} isDark={isDark} />
                  <MetricChip label="FD Proj" value={oneDec(groupBSum.fd.proj)} isDark={isDark} />
                </div>

                <div className="mt-4">
                  <div className={isDark ? "text-xs text-slate-400 mb-2" : "text-xs text-slate-500 mb-2"}>
                    Difference (A − B)
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <MetricChip
                      label="DK Salary"
                      value={money(groupASum.dk.sal - groupBSum.dk.sal)}
                      isBad={groupASum.dk.sal - groupBSum.dk.sal > 0}
                      isDark={isDark}
                    />
                    <MetricChip
                      label="DK Proj"
                      value={oneDec(groupASum.dk.proj - groupBSum.dk.proj)}
                      isBad={false}
                      isDark={isDark}
                    />
                    <MetricChip
                      label="FD Salary"
                      value={money(groupASum.fd.sal - groupBSum.fd.sal)}
                      isBad={groupASum.fd.sal - groupBSum.fd.sal > 0}
                      isDark={isDark}
                    />
                    <MetricChip
                      label="FD Proj"
                      value={oneDec(groupASum.fd.proj - groupBSum.fd.proj)}
                      isBad={false}
                      isDark={isDark}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className={isDark ? "text-xs text-slate-400 mb-2" : "text-xs text-slate-500 mb-2"}>
                    Selected ({groupB.size})
                  </div>
                  <div className="space-y-2 max-h-[24vh] overflow-auto pr-1">
                    {[...groupB]
                      .map((k) => rowByDriver.get(k))
                      .filter(Boolean)
                      .map((r) => (
                        <DriverPill
                          key={keynorm(r?.[driverKey])}
                          name={String(r?.[driverKey] ?? "")}
                          iconSrc={iconForRow(r)}
                          right={
                            siteCols?.dk?.proj
                              ? `DK ${oneDec(num(r?.[siteCols.dk.proj]))}`
                              : null
                          }
                          isDark={isDark}
                          onClick={() => {
                            const k = keynorm(r?.[driverKey]);
                            toggleInSet(setGroupB, groupB, k);
                          }}
                        />
                      ))}
                    {!groupB.size && (
                      <div className={isDark ? "text-xs text-slate-500" : "text-xs text-slate-600"}>
                        No drivers selected.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}


{/* Charts (Scatter Playground) */}
{!loading && !err && view === "charts" && (
  <NascarProjectionCharts
    rows={filteredRows}
              series={series}
    allColsRaw={allColsRaw}
    driverKey={driverKey}
    driverNumMap={driverNumMap}
    isDark={isDark}
    brand={brand}
    siteCols={siteCols}
    cardClass={cardClass}
    setShowDriver={setShowDriver}
    setShowDriverNum={setShowDriverNum}
  />
)}

          {/* Point distribution modal */}
          <PointDistModal
            open={pointDistOpen}
            onClose={closePointDist}
            isDark={isDark}
            site={pointDistSite}
            driverName={pointDistDriverName}
            bins={
  pointDistsIndex
    ? pointDistSite === "DK"
      ? pointDistsIndex.dk?.[pointDistDriverKey]
      : pointDistsIndex.fd?.[pointDistDriverKey]
    : null
}
            proj={
              (() => {
                const row = rowByDriver.get(pointDistDriverKey);
                if (!row) return NaN;
                const col =
                  pointDistSite === "DK" ? siteCols?.dk?.proj : siteCols?.fd?.proj;
                return num(row?.[col]);
              })()
            }
          />

          {/* Modal */}
          <DriverCardModal
            open={!!showDriver}
            onClose={() => setShowDriver(null)}
            row={showDriver}
            driverNum={showDriverNum}
            practiceAvg={showDriver ? practiceAvgMap.get(keynorm(showDriver[driverKey])) : undefined}
            bases={{
              base: BASE,
              numLogoBase: NUM_LOGO_BASE,
              driverPhotoBase: `${BASE}logos/nascar/Drivers`,
              teamLogoBase: `${BASE}logos/nascar/Teams`,
            }}
          />
                  </div>
                </div>
              </div>
            );
          }



/* ------------------------- Charts sub-component ------------------------- */
/**
 * Driver card images:
 * Put your number card PNGs in: /public/driver-cards/<carNum>.png
 * Example: public/driver-cards/5.png => "/driver-cards/5.png"
 */
function NascarProjectionCharts({
  rows,
  series,
  allColsRaw,
  driverKey,
  driverNumMap,
  isDark,
  brand,
  siteCols,
  cardClass,
  setShowDriver,
  setShowDriverNum,
}) {
  const seriesFolder = (() => {
    const k = String(series || "cup").toLowerCase();
    if (k.includes("xfi")) return "Xfinity";
    if (k.includes("truck")) return "Trucks";
    return "Cup";
  })();
  const DRIVER_CARD_URL = (carNum) => `/logos/nascar/${seriesFolder}/${carNum}.png`;

  const [chartPreset, setChartPreset] = React.useState("proj_vs_own");
  const [xCol, setXCol] = React.useState("");
  const [yCol, setYCol] = React.useState("");
  const [zCol, setZCol] = React.useState("");
  const [colorBy, setColorBy] = React.useState("none"); // none | value | start

  // cardStatus: { [carNum]: true (exists) | false (missing) }
  const [cardStatus, setCardStatus] = React.useState(() => ({}));


  const numericCols = React.useMemo(() => {
    const cols = (allColsRaw || []).filter((c) => {
      if (brand === "Both") return true;
      const b = colBrand(c);
      return b === "NEUTRAL" || b === brand;
    });
    return cols.filter((c) => looksNumericCol(c));
  }, [allColsRaw, brand]);

  const chartPresets = React.useMemo(() => {
    const pickSafe = (c, fallbackIdx = 0) => c || numericCols[fallbackIdx] || "";
    const dk = siteCols?.dk || {};
    const fd = siteCols?.fd || {};
    const site = brand === "FD" ? fd : dk;

    const pown = pickSafe(site?.pown, 0);
    const proj = pickSafe(site?.proj, 1);
    const opt = pickSafe(site?.opt, 2);
    const val = pickSafe(site?.val, 3);
    const sal = pickSafe(site?.sal, 4);

    const start =
      pickCol(allColsRaw, [/^qual$/i, /start\s*pos/i, /grid\s*pos/i, /qual\s*pos/i, /^(start|starting)\s*(pos|position)$/i]) ||
      "";
    const ll = pickCol(allColsRaw, [/\bll\b/i, /laps\s*led/i, /p?ll\b/i]) || "";
    const dom = pickCol(allColsRaw, [/\bdom\b/i, /dominator/i]) || "";
    const ceil = pickCol(allColsRaw, [/ceiling/i, /ceil\b/i]) || "";

    return [
      { key: "proj_vs_own", label: "Projection vs Ownership", x: pown, y: proj, z: opt, colorBy: "none" },
      { key: "leverage", label: "Leverage: Ownership vs Ceiling", x: pown, y: pickSafe(ceil, 2), z: opt, colorBy: "none" },
      { key: "salary_eff", label: "Salary vs Projection", x: sal, y: proj, z: val, colorBy: "value" },
      { key: "dom_profile", label: "Start Pos vs Laps Led", x: start, y: pickSafe(ll, 2), z: dom, colorBy: "start" },
    ];
  }, [siteCols, brand, allColsRaw, numericCols]);

  // keep preset -> axes synced
  React.useEffect(() => {
    const p = chartPresets.find((p0) => p0.key === chartPreset) || chartPresets[0];
    if (!p) return;
    setXCol(p.x || "");
    setYCol(p.y || "");
    setZCol(p.z || "");
    setColorBy(p.colorBy || "none");
  }, [chartPreset, chartPresets]);

  const chartData = React.useMemo(() => {
    if (!xCol || !yCol) return [];
    const startCol =
      pickCol(allColsRaw, [/^qual$/i, /start\s*pos/i, /grid\s*pos/i, /qual\s*pos/i, /^(start|starting)\s*(pos|position)$/i]) || "";

    const getStart = (r) => {
      if (!startCol) return NaN;
      const v = r?.[startCol];
      const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
      return Number.isFinite(n) ? n : NaN;
    };

    const out = [];
    for (const r of rows || []) {
      const x = num(r?.[xCol]);
      const y = num(r?.[yCol]);
      const z = zCol ? num(r?.[zCol]) : NaN;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      out.push({
        __row: r,
        __driver: String(r?.[driverKey] ?? ""),
        x,
        y,
        z: Number.isFinite(z) ? z : undefined,
        start: getStart(r),
      });
    }
    return out;
  }, [rows, xCol, yCol, zCol, driverKey, allColsRaw]);

// preload driver cards and cache existence (so we can render image-only dots)
React.useEffect(() => {
  const nums = Array.from(
    new Set(
      (chartData || [])
        .map((d) => {
          const nm = keynorm(d?.__row?.[driverKey]);
          const n = driverNumMap.get(nm);
          return Number.isFinite(n) ? n : null;
        })
        .filter((v) => v !== null)
    )
  );
  const toCheck = nums.filter((n) => cardStatus[n] === undefined);
  if (!toCheck.length) return;

  let cancelled = false;
  toCheck.forEach((n) => {
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setCardStatus((prev) => ({ ...prev, [n]: true }));
    };
    img.onerror = () => {
      if (cancelled) return;
      setCardStatus((prev) => ({ ...prev, [n]: false }));
    };
    img.src = DRIVER_CARD_URL(n);
  });

  return () => {
    cancelled = true;
  };
}, [chartData, driverKey, driverNumMap, cardStatus]);



  const zDomain = React.useMemo(() => {
    const zs = chartData.map((d) => d.z).filter(Number.isFinite);
    if (!zs.length) return [1, 10];
    let mn = Math.min(...zs);
    let mx = Math.max(...zs);
    if (mn === mx) mx = mn + 1;
    return [mn, mx];
  }, [chartData]);

  const avgX = React.useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((s, d) => s + d.x, 0) / chartData.length;
  }, [chartData]);

  const avgY = React.useMemo(() => {
    if (!chartData.length) return null;
    return chartData.reduce((s, d) => s + d.y, 0) / chartData.length;
  }, [chartData]);



const xDomain = React.useMemo(() => {
  if (!chartData.length) return [0, 1];
  const vals = chartData.map((d) => d.x).filter((v) => Number.isFinite(v));
  if (!vals.length) return [0, 1];
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  if (mn === mx) mx = mn + 1;
  const span = mx - mn;
  const pad = span * 0.06; // 6% padding
  mn = mn - pad;
  mx = mx + pad;
  if (mn > 0 && mn < pad) mn = 0;
  return [mn, mx];
}, [chartData]);

const yDomain = React.useMemo(() => {
  if (!chartData.length) return [0, 1];
  const vals = chartData.map((d) => d.y).filter((v) => Number.isFinite(v));
  if (!vals.length) return [0, 1];
  let mn = Math.min(...vals);
  let mx = Math.max(...vals);
  if (mn === mx) mx = mn + 1;
  const span = mx - mn;
  const pad = span * 0.06; // 6% padding
  mn = mn - pad;
  mx = mx + pad;
  if (mn > 0 && mn < pad) mn = 0;
  return [mn, mx];
}, [chartData]);

// Diagonal line through origin with slope = (avgY/avgX), clamped to visible ranges.
const diagSegment = React.useMemo(() => {
  if (!Number.isFinite(avgX) || !Number.isFinite(avgY) || avgX === 0) return null;
  const slope = avgY / avgX;
  const x1 = xDomain[0];
  const x2 = xDomain[1];
  let y1 = slope * x1;
  let y2 = slope * x2;
  const ymin = yDomain[0];
  const ymax = yDomain[1];
  y1 = Math.max(ymin, Math.min(ymax, y1));
  y2 = Math.max(ymin, Math.min(ymax, y2));
  return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
}, [avgX, avgY, xDomain, yDomain]);


  function bucketColor(d) {
    if (colorBy === "value") {
      const v = d.z;
      if (!Number.isFinite(v)) return isDark ? "#38bdf8" : "#0284c7";
      if (v >= zDomain[0] + (zDomain[1] - zDomain[0]) * 0.66) return "#22c55e";
      if (v >= zDomain[0] + (zDomain[1] - zDomain[0]) * 0.33) return "#eab308";
      return "#f97316";
    }
    if (colorBy === "start") {
      const s = d.start;
      if (!Number.isFinite(s)) return isDark ? "#38bdf8" : "#0284c7";
      if (s <= 10) return "#a78bfa";
      if (s <= 20) return "#38bdf8";
      if (s <= 30) return "#22c55e";
      return "#f97316";
    }
    return isDark ? "#38bdf8" : "#0284c7";
  }

  function ChartTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div
        className={
          "rounded-xl border px-3 py-2 text-xs shadow-lg " +
          (isDark ? "bg-slate-950 border-slate-800 text-slate-100" : "bg-white border-slate-200 text-slate-900")
        }
      >
        <div className="font-extrabold mb-1">{d.__driver}</div>
        <div className="tabular-nums">
          {xCol}: <span className="font-semibold">{oneDec(d.x)}</span>
        </div>
        <div className="tabular-nums">
          {yCol}: <span className="font-semibold">{oneDec(d.y)}</span>
        </div>
        {zCol ? (
          <div className="tabular-nums">
            {zCol}: <span className="font-semibold">{oneDec(d.z)}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className={cardClass + " p-4"}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-sm font-extrabold">Charts</div>
            <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-600"}>
              Pick X/Y (and optional bubble size). Dots show <span className="font-semibold">car #</span> and overlay a driver card image if it exists.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-end">
            <div className="flex items-center gap-2">
              <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>Preset</div>
              <select
                value={chartPreset}
                onChange={(e) => setChartPreset(e.target.value)}
                className={
                  "h-8 rounded-full border px-2 text-xs focus:outline-none " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
                }
              >
                {chartPresets.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>X</div>
              <select
                value={xCol}
                onChange={(e) => setXCol(e.target.value)}
                className={
                  "h-8 rounded-full border px-2 text-xs focus:outline-none " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
                }
              >
                {numericCols.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>Y</div>
              <select
                value={yCol}
                onChange={(e) => setYCol(e.target.value)}
                className={
                  "h-8 rounded-full border px-2 text-xs focus:outline-none " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
                }
              >
                {numericCols.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>Bubble</div>
              <select
                value={zCol}
                onChange={(e) => setZCol(e.target.value)}
                className={
                  "h-8 rounded-full border px-2 text-xs focus:outline-none " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
                }
              >
                <option value="">(none)</option>
                {numericCols.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <div className={isDark ? "text-[11px] font-semibold text-slate-300" : "text-[11px] font-semibold text-slate-700"}>Color</div>
              <select
                value={colorBy}
                onChange={(e) => setColorBy(e.target.value)}
                className={
                  "h-8 rounded-full border px-2 text-xs focus:outline-none " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800")
                }
              >
                <option value="none">None</option>
                <option value="value">By Value (uses Bubble)</option>
                <option value="start">By Start Pos</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border overflow-hidden" style={{ height: 520 }}>
          {(!xCol || !yCol) ? (
            <div className={isDark ? "p-4 text-sm text-slate-400" : "p-4 text-sm text-slate-600"}>
              Pick X and Y columns to render a chart.
            </div>
          ) : !chartData.length ? (
            <div className={isDark ? "p-4 text-sm text-slate-400" : "p-4 text-sm text-slate-600"}>
              No points available (check your search/filter or selected columns).
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <XAxis
                  type="number"
                  dataKey="x"
                  name={xCol}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
domain={xDomain} 
  tickFormatter={oneDecMax}
/>
                <YAxis
                  type="number"
                  dataKey="y"
                  name={yCol}
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
domain={yDomain} 
  tickFormatter={oneDecMax}
/>

                {zCol ? (
                  <ZAxis type="number" dataKey="z" name={zCol} domain={zDomain} range={[60, 360]} />
                ) : null}

                {Number.isFinite(avgX) && (
                  <ReferenceLine
                    x={avgX}
                    stroke={isDark ? "#94a3b8" : "#64748b"}
                    strokeDasharray="6 6"
                    strokeOpacity={0.85}
                    ifOverflow="extendDomain"
/>
                )}
                {Number.isFinite(avgY) && (
                  <ReferenceLine
                    y={avgY}
                    stroke={isDark ? "#94a3b8" : "#64748b"}
                    strokeDasharray="6 6"
                    strokeOpacity={0.85}
                    ifOverflow="extendDomain"
/>
                )}

                {/* Average ratio line (passes through the averages) */}
{diagSegment && (
  <ReferenceLine
    segment={diagSegment}
    stroke={isDark ? "#a78bfa" : "#7c3aed"}
    strokeDasharray="4 8"
    strokeOpacity={0.75}
    ifOverflow="extendDomain"
/>
)}

<Tooltip content={<ChartTooltip 
  formatter={(value) => oneDecMax(value)}
  labelFormatter={(label) => oneDecMax(label)}
/>} />

                <Scatter
                  data={chartData}
                  
shape={(props) => {
                    const { cx, cy, payload } = props;

                    const size =
                      zCol && Number.isFinite(payload?.z)
                        ? 6 + ((payload.z - zDomain[0]) / (zDomain[1] - zDomain[0] || 1)) * 10
                        : 8;

                    const fill = bucketColor(payload);

                    const r = payload?.__row;
                    const nm = keynorm(r?.[driverKey]);
                    const carNum = driverNumMap.get(nm);

                    const rad = Math.max(6, Math.min(18, size));
                    const imgW = Math.max(22, Math.min(52, rad * 2.9));
                    const imgH = Math.max(16, Math.min(38, rad * 2.0));

                    const hasCard = Number.isFinite(carNum) && cardStatus?.[carNum] === true;

                    // If card exists: show ONLY the card image.
                    if (hasCard) {
                      return (
                        <g>
                          <image
                            href={DRIVER_CARD_URL(carNum)}
                            x={cx - imgW / 2}
                            y={cy - imgH / 2}
                            width={imgW}
                            height={imgH}
                            preserveAspectRatio="xMidYMid meet"
                            style={{ pointerEvents: "none" }}
                          />
                        </g>
                      );
                    }

                    // Otherwise: fallback to bubble + number.
                    return (
                      <g>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={rad}
                          fill={fill}
                          fillOpacity={0.9}
                          stroke={isDark ? "#0f172a" : "#e2e8f0"}
                          strokeWidth={1}
                        />
                        {Number.isFinite(carNum) && (
                          <text
                            x={cx}
                            y={cy + 1}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={Math.max(10, Math.min(14, rad))}
                            fontWeight={900}
                            fill={isDark ? "#0b1220" : "#0b1220"}
                            style={{ pointerEvents: "none" }}
                          >
                            {carNum}
                          </text>
                        )}
                      </g>
                    );
                  }}
                  onClick={(e) => {
                    const r = e?.payload?.__row;
                    if (!r) return;
                    setShowDriver(r);
                    const nm = keynorm(r?.[driverKey]);
                    const carNum = driverNumMap.get(nm);
                    setShowDriverNum(Number.isFinite(carNum) ? carNum : undefined);
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={isDark ? "mt-3 text-xs text-slate-400" : "mt-3 text-xs text-slate-600"}>
          Tip: use the global search box to filter the plotted driver pool (it applies to this chart too).
        </div>
      </div>
    </div>
  );
}
